import { FC } from 'react';
import { isMobile } from 'react-device-detect';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    CommonModal,
    CommonModalContainer,
    CommonModalProps,
} from '../CommonModal';
import { LiveIndicator } from '../LiveIndicator';
import fireBallGameDetails from '../../assets/apng/fireball-game-details.png';
import { Ball30 } from '../Ball30';
import { Ball75 } from '../Ball75';
import { Ball80 } from '../Ball80';
import { Ball90 } from '../Ball90';
import { GameTypeNumbers } from '../GameType';

type GameParts = {
    description: string;
    prize: string;
    hasJackpot: boolean;
};

type GameInfoModalProps = CommonModalProps & {
    gameId: number;
    gameParts: GameParts[];
    gamePrize: string;
    hasJackpot: boolean;
    jackpotName?: string;
    jackpotCall?: number;
    jackpotPrize?: string;
    gameContinued?: boolean;
    isGameLive: boolean;
    gameType: GameTypeNumbers;
    // The designated Fire Ball number; 0/undefined when not a Fire Ball game.
    fireBall?: number;
    fireBallEnabled?: boolean;
};

const commonStyle = css`
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
`;

const gameDetailsStyle = css`
    font-family: Signika-Bold;
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 3.188rem;
`;

const tableLabelStyle = css`
    font-family: Signika-Bold;
    font-size: 1.75rem;
    font-weight: bold;
    margin-bottom: 0.063rem;
    color: ${({ theme }) => theme.gameInfoModal.color.primary};
`;

const tableValueStyle = css`
    font-family: Signika-Medium;
    font-size: 1.875rem;
`;

const GameInfoModalContainer = styled(CommonModalContainer)`
    display: flex;
    flex-direction: column;
    width: 39.375rem;
    padding: 2.375rem 1.5rem;
    position: relative;

    ${isMobile
        ? ''
        : `
        left: 16.5rem;
        margin-top: 5.25rem;
   `};
`;

const FlexWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const GameDetailsText = styled.div`
    ${commonStyle};
    ${gameDetailsStyle};

    margin-right: 1.438rem;
    color: ${({ theme }) => theme.gameInfoModal.color.primary};
`;

const GameIDText = styled.div`
    ${commonStyle};
    ${gameDetailsStyle};

    color: ${({ theme }) => theme.gameInfoModal.color.secondary};
`;

const DescriptionLabel = styled.div`
    ${commonStyle};
    ${tableLabelStyle};

    flex-basis: 70%;
`;

const JackpotLabel = styled.div`
    ${commonStyle};
    ${tableLabelStyle};

    margin-right: 0.625rem;
    font-size: 1.5rem;
`;

const JackpotName = styled.div`
    ${commonStyle};
    ${tableValueStyle};

    margin-right: 0.625rem;
    font-size: 1.5rem;
    text-wrap: balance;
    color: ${({ theme }) => theme.gameInfoModal.color.secondary};
`;

const PrizeLabel = styled.div`
    ${commonStyle};
    ${tableLabelStyle};

    flex-basis: 30%;
`;

const Divider = styled.hr`
    width: 100%;
    border-width: 0;
    height: 0.063rem;
    margin: 1rem 0 1.313rem 0;
    background-color: ${({ theme }) => theme.gameInfoModal.background.divider};
`;

const NumberDescriptionContainer = styled.div`
    display: flex;
    align-items: center;
    flex-basis: 70%;
`;

const PartNumber = styled.div`
    ${commonStyle};
    font-family: Signika-Bold;
    font-size: 1.75rem;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 2.625rem;
    height: 2.625rem;
    margin-right: 1.313rem;
    border-radius: 50%;

    ${({ theme: { gameInfoModal } }) => `
        color: ${gameInfoModal.color.primary};
        background-color: ${gameInfoModal.background.number};
    `};
`;

const DescriptionValue = styled.div`
    ${commonStyle};
    ${tableValueStyle};

    word-wrap: break-word;
    width: 18rem;
    color: ${({ theme }) => theme.gameInfoModal.color.secondary};
`;

const PrizeValue = styled.div`
    ${commonStyle};
    ${tableValueStyle};

    color: ${({ theme }) => theme.gameInfoModal.color.primary};
`;

const JackpotContainer = styled.div`
    display: flex;
    margin-left: 3.75rem;
    margin-top: 0.938rem;
    white-space: nowrap;
`;

const GameContinuedText = styled.div`
    font-family: Signika-SemiBold;
    font-size: 1.125rem;
    color: ${({ theme }) => theme.gameInfoModal.color.secondary};
`;

export const GameContinuedIcon = styled.img`
    width: 2.5rem;
    height: 2.5rem;
    margin-left: 1rem;
`;

export const LiveIndicatorContainer = styled.div`
    position: absolute;
    right: 1.325rem;
    top: 5.975rem;
`;

const BALL_COMPONENTS = {
    30: Ball30,
    75: Ball75,
    80: Ball80,
    90: Ball90,
};

// Fire Ball
export const FireBallContainer = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 1.313rem;
`;

export const FireBallIconContainer = styled.div`
    position: relative;
    flex: 0 0 auto;
    width: 4.25rem;
    height: 4.25rem;
    margin-right: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const FireBallGameDetailsImage = styled.img`
    position: absolute;
    top: 50%;
    left: 50%;
    width: calc(100% + 40px);
    height: calc(100% + 40px);
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1;
`;

export const FireBallText = styled.div`
    ${commonStyle};
    font-family: Signika-SemiBold;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.gameInfoModal.color.secondary};
`;

export const GameInfoModal: FC<GameInfoModalProps> = ({
    gameId,
    gameParts,
    gamePrize,
    hasJackpot,
    jackpotName,
    jackpotCall,
    jackpotPrize,
    gameContinued,
    isGameLive,
    gameType,
    fireBall,
    fireBallEnabled = true,
    ...props
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { starImage4 } = theme.images;
    const FireBall = BALL_COMPONENTS[gameType];

    return (
        <CommonModal {...props} Container={GameInfoModalContainer}>
            <FlexWrapper>
                <GameDetailsText>{t('infoBox.gameDetails')}</GameDetailsText>
                <GameIDText>
                    {t('infoBox.gameDetailsSID', { sid: gameId })}
                </GameIDText>
            </FlexWrapper>
            {isGameLive && (
                <LiveIndicatorContainer>
                    <LiveIndicator />
                </LiveIndicatorContainer>
            )}
            {fireBallEnabled && !!fireBall && fireBall > 0 && (
                <FireBallContainer data-testid="fireball-info">
                    {FireBall && (
                        <FireBallIconContainer>
                            <FireBall number={fireBall} size="xsmall" />
                            <FireBallGameDetailsImage
                                src={fireBallGameDetails}
                                alt=""
                            />
                        </FireBallIconContainer>
                    )}
                    <FireBallText>
                        {t('infoBox.fireBallActive', { number: fireBall })}
                    </FireBallText>
                </FireBallContainer>
            )}
            <FlexWrapper>
                <DescriptionLabel>
                    {t('infoBox.gameDetailsPartDescription')}
                </DescriptionLabel>
                <PrizeLabel>{t('infoBox.gameDetailsPrize')}</PrizeLabel>
            </FlexWrapper>
            <Divider />
            {gameParts.map((part, index) => {
                return (
                    <div key={index}>
                        <FlexWrapper>
                            <NumberDescriptionContainer>
                                <PartNumber>{index + 1}</PartNumber>
                                <DescriptionValue>
                                    {part.description}
                                </DescriptionValue>
                                {gameContinued && (
                                    <GameContinuedIcon
                                        src={starImage4}
                                        alt="Continued Game Icon"
                                    />
                                )}
                            </NumberDescriptionContainer>
                            <PrizeValue>{part.prize}</PrizeValue>
                        </FlexWrapper>
                        <JackpotContainer>
                            {part.hasJackpot && hasJackpot ? (
                                <>
                                    <JackpotLabel>
                                        {`${t('infoBox.gameDetailsJackpot')}:`}
                                    </JackpotLabel>
                                    <JackpotName>
                                        {t('infoBox.jackpotFormatting', {
                                            jpName: jackpotName,
                                            jpCall: jackpotCall,
                                        }) +
                                            ' ' +
                                            jackpotPrize}
                                    </JackpotName>
                                </>
                            ) : null}
                        </JackpotContainer>
                        <Divider />
                    </div>
                );
            })}
            {gameContinued && (
                <GameContinuedText>
                    {t('infoBox.gameContinued')}
                </GameContinuedText>
            )}
        </CommonModal>
    );
};
